import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import GiveawayCard from '../components/GiveawayCard';
import GiveawayDetails from '../components/GiveawayDetails';
import CreateGiveaway from '../components/CreateGiveaway';
import GiveawayAnalytics from '../components/GiveawayAnalytics';
import BulkGiveawayUpload from '../components/BulkGiveawayUpload';
import RollChecker from '../components/RollChecker';
import { CardSkeleton } from '../components/LoadingSkeleton';
import '../index.css';

const Giveaways = () => {
  const { isAuthenticated, user } = useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ongoing');
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedGiveawayForAnalytics, setSelectedGiveawayForAnalytics] = useState(null);
  const [userEntries, setUserEntries] = useState(new Set());
  const [sortBy, setSortBy] = useState('recent');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 12;

  // State for the deep-linked giveaway
  const [selectedGiveaway, setSelectedGiveaway] = useState(null);
  const [loadingSelected, setLoadingSelected] = useState(false);

  const fetchUserEntries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/giveaways/user/my-entries', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const entryIds = new Set(response.data
        .filter(entry => entry.giveaway && entry.giveaway._id)
        .map(entry => entry.giveaway._id));
      setUserEntries(entryIds);
    } catch (error) {
      console.error('Error fetching user entries:', error);
    }
  }, []);

  const fetchGiveaways = useCallback(async () => {
    try {
      setLoading(true);
      let status;
      if (activeTab === 'ongoing') {
        status = 'ongoing';
      } else if (activeTab === 'closed') {
        status = 'closed';
      } else if (activeTab === 'ended') {
        status = 'ended,rolled';
      }

      const response = await axios.get('/api/giveaways', {
        params: {
          ...(status && { status }),
          sortBy,
          page: currentPage,
          limit: itemsPerPage
        }
      });

      // Handle new paginated response format
      if (response.data.giveaways) {
        let filtered = response.data.giveaways;
        if (activeTab === 'ended') {
          filtered = response.data.giveaways.filter(g => g.status === 'ended' || g.status === 'rolled');
        } else if (activeTab === 'closed') {
          filtered = response.data.giveaways.filter(g => g.status === 'closed');
        } else if (activeTab === 'ongoing') {
          filtered = response.data.giveaways.filter(g => g.status === 'ongoing');
        }

        setGiveaways(filtered);
        setTotalPages(response.data.pagination.totalPages);
        setTotalCount(response.data.pagination.totalCount);
      } else {
        // Fallback for old response format (without pagination)
        let filtered = response.data;
        if (activeTab === 'ended') {
          filtered = response.data.filter(g => g.status === 'ended' || g.status === 'rolled');
        } else if (activeTab === 'closed') {
          filtered = response.data.filter(g => g.status === 'closed');
        } else if (activeTab === 'ongoing') {
          filtered = response.data.filter(g => g.status === 'ongoing');
        }

        setGiveaways(filtered);
        setTotalPages(1);
        setTotalCount(filtered.length);
      }
    } catch (error) {
      console.error('Error fetching giveaways:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, sortBy, currentPage]);

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when tab or sort changes
  }, [activeTab, sortBy]);

  useEffect(() => {
    fetchGiveaways();
    if (isAuthenticated) {
      fetchUserEntries();
    }
  }, [activeTab, isAuthenticated, fetchGiveaways, fetchUserEntries, currentPage]);

  // Handle deep linking to specific giveaway
  useEffect(() => {
    if (id) {
      const fetchSingleGiveaway = async () => {
        try {
          setLoadingSelected(true);
          const response = await axios.get(`/api/giveaways/${id}`);
          setSelectedGiveaway(response.data);
        } catch (error) {
          console.error('Error fetching deep-linked giveaway:', error);
          navigate('/giveaways');
        } finally {
          setLoadingSelected(false);
        }
      };
      fetchSingleGiveaway();
    } else {
      setSelectedGiveaway(null);
    }
  }, [id, navigate]);

  const handleGiveawayCreated = () => {
    setShowCreateModal(false);
    fetchGiveaways();
  };

  const handleGiveawayEntered = () => {
    fetchGiveaways();
    if (isAuthenticated) {
      fetchUserEntries();
    }
  };

  const handleViewAnalytics = (giveawayId) => {
    setSelectedGiveawayForAnalytics(giveawayId);
    setShowAnalyticsModal(true);
  };

  return (
    <div className="giveaways-page">
      <div style={{ marginBottom: '30px' }}>
        <div className="giveaways-header">
          <h1 className="giveaways-title">🎁 Giveaways</h1>
          {isAuthenticated && (
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowCreateModal(true)}
                className="host-giveaway-btn"
              >
                + Host a Giveaway
              </button>
              {user?.trustedGiveawayHost && (
                <button
                  onClick={() => setShowBulkUploadModal(true)}
                  style={{
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📦 Bulk Upload
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="giveaways-tabs" style={{ marginTop: '30px' }}>
          <button
            onClick={() => setActiveTab('ongoing')}
            className={`tab-button ${activeTab === 'ongoing' ? 'active' : ''}`}
          >
            🔥 Ongoing
          </button>
          <button
            onClick={() => setActiveTab('closed')}
            className={`tab-button ${activeTab === 'closed' ? 'active' : ''}`}
          >
            🔒 Closed
          </button>
          <button
            onClick={() => setActiveTab('ended')}
            className={`tab-button ${activeTab === 'ended' ? 'active' : ''}`}
          >
            ⏱️ Ended
          </button>
          <button
            onClick={() => setActiveTab('checker')}
            className={`tab-button ${activeTab === 'checker' ? 'active' : ''}`}
          >
            🔍 Roll Checker
          </button>
        </div>

        {/* Sort Options */}
        {activeTab !== 'checker' && (
          <div style={{
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <span style={{
              color: 'var(--text-secondary)',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Sort by:
            </span>
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setSortBy('recent')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: sortBy === 'recent' ? '2px solid var(--primary)' : '1px solid var(--border)',
                  backgroundColor: sortBy === 'recent' ? 'rgba(102, 126, 234, 0.1)' : 'var(--card-bg)',
                  color: sortBy === 'recent' ? 'var(--primary)' : 'var(--text)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                📅 Recent
              </button>
              <button
                onClick={() => setSortBy('entries')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: sortBy === 'entries' ? '2px solid var(--primary)' : '1px solid var(--border)',
                  backgroundColor: sortBy === 'entries' ? 'rgba(102, 126, 234, 0.1)' : 'var(--card-bg)',
                  color: sortBy === 'entries' ? 'var(--primary)' : 'var(--text)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                🔥 Most Entries
              </button>
              <button
                onClick={() => setSortBy('ending')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: sortBy === 'ending' ? '2px solid var(--primary)' : '1px solid var(--border)',
                  backgroundColor: sortBy === 'ending' ? 'rgba(102, 126, 234, 0.1)' : 'var(--card-bg)',
                  color: sortBy === 'ending' ? 'var(--primary)' : 'var(--text)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                ⏰ Ending Soon
              </button>
              <button
                onClick={() => setSortBy('recently-ended')}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  border: sortBy === 'recently-ended' ? '2px solid var(--primary)' : '1px solid var(--border)',
                  backgroundColor: sortBy === 'recently-ended' ? 'rgba(102, 126, 234, 0.1)' : 'var(--card-bg)',
                  color: sortBy === 'recently-ended' ? 'var(--primary)' : 'var(--text)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                ⏱️ Recently Ended
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {activeTab === 'checker' ? (
        <RollChecker />
      ) : (
        <div>
          {loading ? (
            <div className="giveaways-loading">
              <CardSkeleton count={6} />
            </div>
          ) : giveaways.length === 0 ? (
            <div className="giveaways-empty">
              <h3>
                {activeTab === 'ongoing'
                  ? '🎭 No ongoing giveaways at the moment'
                  : activeTab === 'closed'
                    ? '🔒 No closed giveaways'
                    : '📦 No ended giveaways yet'}
              </h3>
              <p>
                {activeTab === 'ongoing' && isAuthenticated
                  ? 'Be the first to create one!'
                  : 'Check back later for new giveaways'}
              </p>
            </div>
          ) : (
            <>
              <div className="giveaways-grid">
                {giveaways.map((giveaway, index) => (
                  <React.Fragment key={giveaway._id}>
                    <GiveawayCard
                      giveaway={giveaway}
                      hasEntered={userEntries.has(giveaway._id)}
                      onEntryComplete={handleGiveawayEntered}
                      onViewAnalytics={handleViewAnalytics}
                    />
                  </React.Fragment>
                ))}              </div>

              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '12px',
                  marginTop: '40px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      backgroundColor: currentPage === 1 ? 'var(--card-bg-secondary)' : 'var(--card-bg)',
                      color: currentPage === 1 ? 'var(--text-secondary)' : 'var(--text)',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: currentPage === 1 ? 0.5 : 1
                    }}
                  >
                    ← Previous
                  </button>

                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                  }}>
                    {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          style={{
                            padding: '10px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            borderRadius: '8px',
                            border: currentPage === pageNum ? '2px solid var(--primary)' : '1px solid var(--border)',
                            backgroundColor: currentPage === pageNum ? 'rgba(102, 126, 234, 0.1)' : 'var(--card-bg)',
                            color: currentPage === pageNum ? 'var(--primary)' : 'var(--text)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            minWidth: '44px'
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      backgroundColor: currentPage === totalPages ? 'var(--card-bg-secondary)' : 'var(--card-bg)',
                      color: currentPage === totalPages ? 'var(--text-secondary)' : 'var(--text)',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: currentPage === totalPages ? 0.5 : 1
                    }}
                  >
                    Next →
                  </button>

                  <div style={{
                    marginLeft: '16px',
                    color: 'var(--text-secondary)',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Page {currentPage} of {totalPages} ({totalCount} total)
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Giveaway Rules Section - Moved to bottom */}
      {activeTab !== 'checker' && (
        <div style={{
          marginTop: '50px',
          marginBottom: '40px',
          padding: '30px',
          backgroundColor: 'var(--card-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          lineHeight: '1.7'
        }}>
          <h2 style={{
            color: 'var(--text-accent)',
            fontSize: '24px',
            fontWeight: '700',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            📋 How Giveaways Work
          </h2>

          <div style={{ color: 'var(--text)', fontSize: '15px' }}>
            <p style={{ marginBottom: '16px' }}>
              Simple giveaway system. Anyone can host, anyone can enter. Here's what you need to know:
            </p>

            <h3 style={{
              color: 'var(--primary)',
              fontSize: '18px',
              fontWeight: '600',
              marginTop: '24px',
              marginBottom: '12px'
            }}>
              🎯 Entering Giveaways
            </h3>

            <div style={{ paddingLeft: '20px', marginBottom: '20px' }}>
              <p style={{ marginBottom: '12px' }}>
                Browse giveaways, click "Enter" on ones you like. Most cost 10-50 points to enter (keeps out spam bots).
                You can only enter once per account, making multiple accounts gets you banned. Winners are picked randomly
                when the giveaway ends. If you win, you get emailed and notified. Usually you have 48-72 hours to claim
                before someone else is picked.
              </p>
            </div>

            <h3 style={{
              color: 'var(--primary)',
              fontSize: '18px',
              fontWeight: '600',
              marginTop: '24px',
              marginBottom: '12px'
            }}>
              🎁 Hosting Giveaways
            </h3>

            <div style={{ paddingLeft: '20px', marginBottom: '20px' }}>
              <p style={{ marginBottom: '12px' }}>
                Any registered user can host. Great for developers to promote games or anyone wanting to give back.
                Set up the prize, entry cost (0-100 points), duration (1-30 days), and number of winners. When it ends,
                winners get picked randomly and you deliver the prize. You have to actually deliver what you promised, fake
                giveaways get you permanently banned.
              </p>
            </div>

            <h3 style={{
              color: 'var(--primary)',
              fontSize: '18px',
              fontWeight: '600',
              marginTop: '24px',
              marginBottom: '12px'
            }}>
              ⚖️ The Rules
            </h3>

            <div style={{ paddingLeft: '20px', marginBottom: '20px' }}>
              <p style={{ marginBottom: '12px' }}>
                Don't use multiple accounts. Don't host fake giveaways. Don't bot entries. Don't harass people. Pretty basic stuff.
                If something goes wrong with a giveaway, report it and I'll look into it. If you can't work it out with the host,
                contact me and I'll help mediate.
              </p>
            </div>

            <div style={{
              marginTop: '24px',
              padding: '20px',
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '8px',
              border: '1px solid var(--primary)'
            }}>
              <p style={{ marginBottom: '0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                <strong style={{ color: 'var(--primary)' }}>💡 Tip:</strong> If you're hosting, make your giveaway clear and promote
                it in community cards. If you're entering, actually engage with the community instead of just farming entries.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create Giveaway Modal */}
      {showCreateModal && (
        <CreateGiveaway
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleGiveawayCreated}
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkGiveawayUpload
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={handleGiveawayCreated}
        />
      )}

      {/* Analytics Modal */}
      {showAnalyticsModal && selectedGiveawayForAnalytics && (
        <GiveawayAnalytics
          giveawayId={selectedGiveawayForAnalytics}
          onClose={() => {
            setShowAnalyticsModal(false);
            setSelectedGiveawayForAnalytics(null);
          }}
        />
      )}

      {/* Deep-linked Giveaway Modal */}
      {selectedGiveaway && (
        <GiveawayDetails
          giveaway={selectedGiveaway}
          hasEntered={userEntries.has(selectedGiveaway._id)}
          isHost={isAuthenticated && user && String(selectedGiveaway.host.userId) === String(user._id)}
          onClose={() => {
            setSelectedGiveaway(null);
            navigate('/giveaways');
          }}
          onEntryComplete={() => {
            setSelectedGiveaway(null);
            navigate('/giveaways');
            handleGiveawayEntered();
          }}
          onViewAnalytics={handleViewAnalytics}
        />
      )}

      {loadingSelected && (
        <div className="modal-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default Giveaways;
